/**
 * Guided tours (#234): step-by-step walkthroughs of Character Home, each of
 * its tabs, the gathering and the intro Crucible's HUD, shown to a new
 * account once. Their steps live with the client; how far the account has
 * come in each is an account setting (`completedTours`), kept per tour with
 * the tour's version, so a reworked tour shows again.
 */
export const TOUR_IDS = [
    'character-home',
    'tab-overview',
    'tab-inventory',
    'tab-skills',
    'tab-crucibles',
    'tab-crafting',
    'tab-journey',
    'gathering',
    'run-hud',
] as const;
export type TourId = typeof TOUR_IDS[number];

export function isTourId(value: unknown): value is TourId {
    return typeof value === 'string' && (TOUR_IDS as readonly string[]).includes(value);
}

/** How far an account has come in one tour. */
export interface TourProgress {
    /** The tour's version when this was saved; an older one counts as not started. */
    readonly version: number;
    /** The step to resume at (0-based), or null once the tour is done or skipped. */
    readonly step: number | null;
}

/** Each tour's progress, by tour id; a tour left out has not started. */
export type CompletedTours = Partial<Record<TourId, TourProgress>>;

function isTourProgress(value: unknown): value is TourProgress {
    const progress = value as Partial<TourProgress> | null;
    return typeof progress === 'object' && progress !== null
        && Number.isInteger(progress.version) && (progress.version as number) >= 1
        && (progress.step === null || (Number.isInteger(progress.step) && (progress.step as number) >= 0));
}

/**
 * Validates tour progress (from a PATCH body or a stored setting): known
 * tour ids, each with a version and a step. Returns a copy, or undefined when
 * it isn't one.
 */
export function parseCompletedTours(value: unknown): CompletedTours | undefined {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return undefined;
    }
    const tours: CompletedTours = {};
    for (const [tourId, progress] of Object.entries(value)) {
        if (!isTourId(tourId) || !isTourProgress(progress)) {
            return undefined;
        }
        tours[tourId] = { version: progress.version, step: progress.step };
    }
    return tours;
}

/** Whether a tour at `version` is done (finished or skipped) by this progress. */
export function isTourDone(tours: CompletedTours, tourId: TourId, version: number): boolean {
    const progress = tours[tourId];
    return progress !== undefined && progress.version >= version && progress.step === null;
}

/** The step a tour at `version` resumes at: where it was left, or 0 when it never started or has been reworked since. */
export function resolveTourStartStep(tours: CompletedTours, tourId: TourId, version: number): number {
    const progress = tours[tourId];
    return progress !== undefined && progress.version >= version && progress.step !== null ? progress.step : 0;
}
